// /src/components/tools/memberProfileCard/PerkThemeMediaLayer.jsx

import React, { useEffect, useRef } from 'react';
import Hls from 'hls.js';
import { isHlsMedia } from '../../../utils/memberProfileThemeDisplay';

/**
 * Full-bleed theme media (image or HLS) under profile/roster surfaces.
 */
export default function PerkThemeMediaLayer({
  url,
  mediaType,
  className = '',
  imageClassName = '',
  videoClassName = '',
  cover = true,
  darken = 0,
}) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const src = String(url || '').trim();
  const hls = isHlsMedia(src, mediaType);
  const darkenPct = Math.max(0, Math.min(100, Math.round(Number(darken) || 0)));

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src || !hls) return undefined;

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      video.play().catch(() => {});
      return () => {
        video.removeAttribute('src');
        video.load();
      };
    }

    if (!Hls.isSupported()) return undefined;

    const instance = new Hls({
      startLevel: 0,
      autoStartLoad: true,
      capLevelToPlayerSize: true,
      maxBufferLength: 4,
    });
    instance.on(Hls.Events.MEDIA_ATTACHED, () => instance.loadSource(src));
    instance.on(Hls.Events.MANIFEST_PARSED, () => {
      video.play().catch(() => {});
    });
    instance.on(Hls.Events.ERROR, (_event, data) => {
      if (!data?.fatal) return;
      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) instance.startLoad();
      else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) instance.recoverMediaError();
      else {
        instance.destroy();
        hlsRef.current = null;
      }
    });
    instance.attachMedia(video);
    hlsRef.current = instance;

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      video.removeAttribute('src');
      video.load();
    };
  }, [src, hls]);

  if (!src) return null;

  return (
    <div className={`perk-theme-media ${cover ? 'perk-theme-media--cover' : ''} ${className}`.trim()} aria-hidden>
      {hls ? (
        <video
          ref={videoRef}
          className={`perk-theme-media__video ${videoClassName}`.trim()}
          muted
          loop
          playsInline
          autoPlay
        />
      ) : (
        <img
          className={`perk-theme-media__img ${imageClassName}`.trim()}
          src={src}
          alt=""
        />
      )}
      {hls && darkenPct > 0 ? (
        <div
          className={`perk-theme-media__darken darken-${darkenPct}`}
          aria-hidden
        />
      ) : null}
    </div>
  );
}
